import React, { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { formatCurrency } from '../utils/helpers';
import { getChartPalette } from '../utils/chartColors';
import { usePreferences } from '../contexts/PreferencesContext';

interface PortfolioPerformanceChartProps {
  currentValue: number;
  costBasis: number;
  period?: '1W' | '1M' | '3M' | '6M' | '1Y' | 'ALL';
}

const PortfolioPerformanceChart: React.FC<PortfolioPerformanceChartProps> = ({
  currentValue,
  costBasis,
  period = '1M',
}) => {
  const { preferences } = usePreferences();
  const palette = getChartPalette(preferences.display.chartColorScheme);
  // Generate simulated historical data based on current portfolio value
  // In a real app, this would come from stored historical snapshots
  const data = useMemo(() => {
    const periodDays: Record<string, number> = {
      '1W': 7,
      '1M': 30,
      '3M': 90,
      '6M': 180,
      '1Y': 365,
      ALL: 730,
    };

    const days = periodDays[period] || 30;
    const result = [];
    const now = new Date();

    // Generate data points with realistic variations
    for (let i = days; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);

      // Simulate portfolio growth from cost basis to current value with some volatility
      const progress = 1 - i / days;
      const baseValue = costBasis + (currentValue - costBasis) * progress;

      // Add some realistic volatility (up to 5% daily fluctuation)
      const volatility = 0.05;
      const randomFactor = 1 + (Math.random() - 0.5) * volatility * 2;
      const value = Math.max(baseValue * randomFactor, costBasis * 0.7);

      // Only add data points at appropriate intervals
      const shouldAdd =
        period === '1W' ||
        (period === '1M' && (i % 1 === 0 || i === 0)) ||
        (period === '3M' && (i % 3 === 0 || i === 0)) ||
        (period === '6M' && (i % 7 === 0 || i === 0)) ||
        (period === '1Y' && (i % 14 === 0 || i === 0)) ||
        (period === 'ALL' && (i % 30 === 0 || i === 0));

      if (shouldAdd || i === 0) {
        result.push({
          date: date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
          }),
          value: i === 0 ? currentValue : value,
          benchmark: costBasis * (1 + progress * 0.1), // S&P 500 ~10% annual benchmark
        });
      }
    }

    return result;
  }, [currentValue, costBasis, period]);

  const isPositive = currentValue >= costBasis;
  const gradientColor = isPositive ? palette.positive : palette.negative;

  return (
    <div className="h-48 lg:h-72 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="portfolioGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={gradientColor} stopOpacity={0.3} />
              <stop offset="95%" stopColor={gradientColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fill: '#94A3B8', fontSize: 11 }}
            axisLine={{ stroke: '#334155' }}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fill: '#94A3B8', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
            domain={['dataMin - 1000', 'dataMax + 1000']}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: palette.tooltip,
              border: '1px solid #334155',
              borderRadius: '8px',
              fontSize: '12px',
            }}
            labelStyle={{ color: '#94A3B8' }}
            formatter={(value: number) => [formatCurrency(value), 'Portfolio']}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke={gradientColor}
            strokeWidth={2}
            fill="url(#portfolioGradient)"
            dot={false}
            activeDot={{ r: 4, fill: gradientColor }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PortfolioPerformanceChart;
